import {withAuthenticationRequired} from '@auth0/auth0-react';
import {PencilIcon, TrashIcon} from '@heroicons/react/outline';
import {FC, useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Column, Row} from 'react-table';
import styled from 'styled-components';
import {IOrganization, TUserStatus, useAdminOrganizations, useUpdateAdminOrganization} from '../../API';
import {useDeleteOrganization} from '../../API/Mutations/useDeleteOrganization';
import {FlexRow, FullPageLoader, H4, StandardButton} from '../../Components';
import {getConfig} from '../../getConfig';
import {requireAdmin} from '../../Managers/AuthUtils';
import {Header} from '../Map/Header';
import {AdminMenu} from './AdminMenu';
import {AdminTable} from './AdminTable';
import {UserStatusCell} from './AdminTable/UserStatusCell';
import {AdminPage, AdminPageDiv, AdminPageWrapper, AdminRow} from './Layout';
import {AdminSummaryBlock} from './SummaryBlock';
import {CSVDownload} from '../../Components/Molecules/CSVDownload';

const {subscriptionManagementEnabled} = getConfig();

const ALLOW_DELETE = false;
const ActionsBlock = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const ActionButton = styled(StandardButton)`
  width: 52px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const findOrgAdmin = (org: IOrganization) => {
  return org.users?.find((s) => {
    return org?.subscription?.stripe_customer_id === s.stripe_customer_id;
  });
};

const OrganizationsComponent: FC = () => {
  const organizations = useAdminOrganizations();
  const [activeSwitch, setActiveSwitch] = useState(0);

  const navigate = useNavigate();
  const deleteOrganization = useDeleteOrganization();
  const updateOrganization = useUpdateAdminOrganization();

  const filteredOrg = useMemo(() => {
    if (activeSwitch === 1) {
      return organizations.data?.filter((u) => (subscriptionManagementEnabled ? !!u.subscription : u!.status === 'active'));
    }
    if (activeSwitch === 2) {
      return organizations.data?.filter((u) => (subscriptionManagementEnabled ? !u.subscription : u!.status !== 'active'));
    }
    return organizations.data;
  }, [organizations.data, activeSwitch]);

  const handleDeleteOrganization = (id: string) => {
    deleteOrganization.mutateAsync(id);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Actions',
        accessor: 'id',
        Cell: ({value, row}: {value: string; row: Row}) => (
          <ActionsBlock>
            <ActionButton onClick={() => navigate(`/admin/organizations/${value}`)}>
              <PencilIcon style={{width: 24}} />
            </ActionButton>
            {ALLOW_DELETE && (
              <ActionButton
                disabled={deleteOrganization?.isLoading}
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete ' + row.values.name + '? This cannot be undone!')) {
                    handleDeleteOrganization(row.values.id);
                  }
                }}>
                <TrashIcon style={{width: 24}} />
              </ActionButton>
            )}
          </ActionsBlock>
        ),
        disableSortBy: true,
      },
      {
        Header: 'Names',
        accessor: 'name',
        sortType: 'string',
        minWidth: 200,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({value, row}: {value: string; row: {original: IOrganization}}) => (
          <UserStatusCell
            value={value}
            row={row}
            // disabled={saving}
            onChange={handleChangeOrganizationStatus}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: 'Address',
        accessor: (row: IOrganization) => `${row.address1} ${row.address2} ${row.city} ${row.state} ${row.zip}`,
        Cell: ({row}: {row: any}) => (
          <div style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
            {row.original.address1} {row.original.address2} {row.original.city} {row.original.state} {row.original.zip}
          </div>
        ),
        minWidth: 200,
      },
      {
        Header: 'Members',
        id: 'members',
        accessor: (row: IOrganization) => row.users?.length ?? '-',
        sortType: 'number',
        minWidth: 100,
      },
      {
        Header: 'Products',
        accessor: (row: IOrganization) => row.authorizations?.length ?? '-',
        minWidth: 100,
      },
      {
        Header: 'Max Users',
        accessor: 'max_users',
        minWidth: 100,
      },
      {
        Header: 'Primary Email',
        accessor: 'primary_email',
        minWidth: 200,
      },
      ...(subscriptionManagementEnabled
        ? [
            {
              Header: 'Org Admin',
              accessor: (row: IOrganization) =>
                row.users?.find((u) => u.stripe_customer_id === row.subscription?.stripe_customer_id)?.email ?? '-',
              Cell: ({row, value}: {row: any; value: string}) => {
                // const user = row.original.users?.find((u: IUser) => u.stripe_customer_id);
                const user = findOrgAdmin(row.original);
                return (
                  <div>
                    <div>
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{value}</div>
                  </div>
                );
              },
              minWidth: 200,
            },
            {
              Header: 'Subscription Type',
              id: 'type',
              accessor: (row: IOrganization) => row.subscription?.type,
              minWidth: 200,
              Cell: ({value, row}: {value: string; row: {original: IOrganization}}) => {
                return <>{value || '-'}</>;
              },
            },
            {
              Header: 'Subscription Status',
              id: 'status',
              accessor: (row: IOrganization) => row.subscription?.status,
              minWidth: 200,
              Cell: ({value, row}: {value: string; row: {original: IOrganization}}) => {
                return <>{value || '-'}</>;
              },
            },
            {
              Header: 'Subscription Peroid Start',
              id: 'current_period_start',
              accessor: (row: IOrganization) => row.subscription?.current_period_start,
              minWidth: 200,
              Cell: ({value, row}: {value: string; row: {original: IOrganization}}) => {
                return <>{value ? new Date(value).toLocaleDateString() : '-'}</>;
              },
            },
            {
              Header: 'Subscription Period End',
              id: 'current_period_end',
              accessor: (row: IOrganization) => row.subscription?.current_period_end,
              minWidth: 200,
              Cell: ({value, row}: {value: string; row: {original: IOrganization}}) => {
                return <>{value ? new Date(value).toLocaleDateString() : '-'}</>;
              },
            },
          ]
        : ([] as Column[])),
    ],
    [subscriptionManagementEnabled],
  );

  const handleChangeOrganizationStatus = useCallback(
    async (value: string, row: any) => {
      await updateOrganization.mutateAsync({
        id: row.original.id,
        status: value as TUserStatus,
      });
    },
    [updateOrganization],
  );

  const csvData = useMemo(() => {
    if (!filteredOrg) return [];
    return filteredOrg.map((org) => [
      org.id,
      org.name,
      org.status,
      [org.address1, org.address2].filter(Boolean).join(' '),
      org.city,
      org.state,
      org.zip,
      org.users?.length,
      org.authorizations?.length,
      org.primary_email,
    ]);
  }, [filteredOrg]);

  const csvHeaders = ['ID', 'Name', 'Status', 'Address', 'City', 'State', 'Zip', 'Members', 'Products', 'Primary Email'];

  const currentTime = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });

  return (
    <AdminPageWrapper>
      <Header />

      <AdminRow>
        <div style={{display: 'flex', flex: 0, justifyContent: 'flex-end', gap: 20}}>
          <AdminMenu />
        </div>

        <AdminPageDiv style={{flex: 1}}>
          <FlexRow style={{justifyContent: 'space-between'}}>
            <H4 style={{margin: 0}}>Organizations</H4>
            <CSVDownload
              headers={csvHeaders}
              data={csvData}
              filename={`organizations-${currentTime}.csv`}
              buttonText="Export Organizations"
            />
          </FlexRow>

          <AdminSummaryBlock />

          {organizations.isLoading || !organizations.data ? (
            <AdminPage>
              <FullPageLoader />
            </AdminPage>
          ) : (
            <AdminTable
              columns={columns}
              data={filteredOrg || []}
              showAddButton
              // onDropdownSelect={(value) => setActiveSwitch(value.value)}
              toggleValue={activeSwitch}
              toggleLabels={['All', 'Active', 'Inactive']}
              onToggleSelect={(value: number) => setActiveSwitch(value)}
              // dropdownOptions={[
              //   {label: 'All', value: 'all'},
              //   {label: 'Active', value: 'active'},
              //   {label: 'Inactive', value: 'inactive'},
              // ]}
            />
          )}
        </AdminPageDiv>
      </AdminRow>
    </AdminPageWrapper>
  );
};

export const Organizations: FC = withAuthenticationRequired(OrganizationsComponent, {claimCheck: requireAdmin});
